/**
 * Service Selection Screen - Page d'accueil réservation
 * Charge la configuration des services depuis l'API
 * 3 types de services:
 * 1. Transfert (transferts, inclut aéroport/gare)
 * 2. Mise à disposition (location avec chauffeur)
 * 3. Excursion (visites guidées)
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Car, Clock, MapPin, ChevronRight, Phone, Plane, Loader2 } from 'lucide-react';
import axios from 'axios';
import config from 'config';

const API_URL = config.API_URL;

// Mapping des icônes
const serviceIcons = {
  transfer: Car,
  local: Car,
  hourly: Clock,
  excursion: MapPin
};

// Configuration par défaut (fallback si API échoue)
const DEFAULT_SERVICES = [
  {
    service_id: 'local',
    name: 'Transfert',
    description: 'Trajets quotidiens, transferts aéroport/gare, déplacements professionnels.',
    highlight: 'Aéroports • Gares • Ville',
    is_active: true,
    features: ['Tarif zone ou km', 'Suivi vols en temps réel', 'Devis immédiat'],
  },
  {
    service_id: 'hourly',
    name: 'Mise à disposition',
    description: 'Louez un véhicule avec chauffeur pour plusieurs heures.',
    highlight: 'De 1h à 12h',
    is_active: true,
    features: ['Tarif horaire', 'Kilométrage inclus', 'Flexibilité totale'],
  },
  {
    service_id: 'excursion',
    name: 'Excursion',
    description: 'Excursions privées : Étretat, Honfleur, plages du Débarquement...',
    highlight: 'Normandie',
    is_active: false,
    redirect_to: '/contact',
    features: ['Itinéraire sur mesure', 'Chauffeur-guide', 'Devis personnalisé'],
  },
];

const ServiceSelectionScreen = ({ onSelectService }) => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  // Charger la configuration des services depuis l'API
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/tariffs/services`);
        if (response.data.services && response.data.services.length > 0) {
          setServices(response.data.services);
        } else {
          setServices(DEFAULT_SERVICES);
        }
      } catch (err) {
        console.error('Error loading services config:', err);
        setServices(DEFAULT_SERVICES);
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  const handleSelect = (service) => {
    if (!service.is_active && service.redirect_to) {
      return service.redirect_to;
    }
    if (service.is_active) {
      // Convertir local -> local pour compatibilité avec le reste du code
      onSelectService(service.service_id === 'transfer' ? 'local' : service.service_id);
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-[#C9A24D] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <p className="text-xs font-semibold tracking-[0.15em] uppercase text-[#C9A24D] mb-3">
          Réservation en ligne
        </p>
        <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl font-bold text-[#F5F5F5] mb-3">
          Quel service recherchez-vous ?
        </h2>
        <p className="text-[#A7A7A7]">
          Sélectionnez le type de prestation
        </p>
      </div>

      <div className="space-y-4">
        {services.map((service) => {
          const Icon = serviceIcons[service.service_id] || Car;
          const isAvailable = service.is_active;
          
          // Service indisponible avec redirection
          if (!isAvailable && service.redirect_to) {
            return (
              <Link
                key={service.service_id}
                to={service.redirect_to}
                data-testid={`service-option-${service.service_id}`}
                className="block group"
              >
                <div className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.12),rgba(10,8,5,0.96))] border border-[#C9A24D]/20 hover:border-[#C9A24D]/50 p-6 transition-all duration-300">
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 bg-[#C9A24D]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#C9A24D]/20 transition-colors">
                      <Icon className="w-7 h-7 text-[#C9A24D]" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#F5F5F5] group-hover:text-[#C9A24D] transition-colors">
                          {service.name}
                        </h3>
                      </div>
                      <p className="text-[#C9A24D] text-sm mb-2">{service.highlight}</p>
                      <p className="text-[#E5E5E5]/60 text-sm mb-3">{service.description}</p>
                      
                      <div className="flex flex-wrap gap-2">
                        {(service.features || []).map((feature, idx) => (
                          <span 
                            key={idx}
                            className="text-xs px-2 py-1 bg-[#0B0B0B]/50 text-[#E5E5E5]/70 border border-[#C9A24D]/10"
                          >
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 text-[#C9A24D]">
                      <Phone className="w-4 h-4" />
                      <span className="text-sm">Contact</span>
                      <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              </Link>
            );
          }
          
          // Service désactivé (pas de redirection)
          if (!isAvailable) {
            return (
              <div
                key={service.service_id}
                data-testid={`service-option-${service.service_id}`}
                className="opacity-50 cursor-not-allowed"
              >
                <div className="bg-[#0B0B0B] border border-[#E5E5E5]/10 p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 bg-[#E5E5E5]/5 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-7 h-7 text-[#E5E5E5]/30" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#E5E5E5]/50">
                          {service.name}
                        </h3>
                        <span className="text-xs px-2 py-0.5 bg-red-500/20 text-red-400">
                          Indisponible
                        </span>
                      </div>
                      <p className="text-[#E5E5E5]/30 text-sm mb-2">{service.highlight}</p>
                      <p className="text-[#E5E5E5]/30 text-sm">{service.description}</p>
                    </div>
                  </div>
                </div>
              </div>
            );
          }
          
          // Service disponible
          return (
            <button
              key={service.service_id}
              onClick={() => handleSelect(service)}
              data-testid={`service-option-${service.service_id}`}
              className="w-full text-left group"
            >
              <div className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] border border-[#C9A24D]/20 hover:border-[#C9A24D]/60 p-6 transition-all duration-300 hover:shadow-[0_22px_55px_rgba(0,0,0,0.85)]">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-[#C9A24D]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#C9A24D]/20 transition-colors">
                    <Icon className="w-7 h-7 text-[#C9A24D]" />
                    {service.service_id === 'local' && (
                      <Plane className="w-4 h-4 text-[#C9A24D]/60 absolute -bottom-1 -right-1" />
                    )}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#F5F5F5] group-hover:text-[#C9A24D] transition-colors">
                        {service.name}
                      </h3>
                      <span className="text-xs px-2 py-0.5 bg-[#C9A24D]/20 text-[#C9A24D] font-medium">
                        Disponible
                      </span>
                    </div>
                    <p className="text-[#C9A24D] text-sm mb-2">{service.highlight}</p>
                    <p className="text-[#E5E5E5]/60 text-sm mb-3">{service.description}</p>
                    
                    <div className="flex flex-wrap gap-2">
                      {(service.features || []).map((feature, idx) => (
                        <span 
                          key={idx}
                          className="text-xs px-2 py-1 bg-[#0B0B0B]/50 text-[#E5E5E5]/70 border border-[#C9A24D]/10"
                        >
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>

                  <ChevronRight className="w-6 h-6 text-[#C9A24D] opacity-50 group-hover:opacity-100 group-hover:translate-x-1 transition-all flex-shrink-0" />
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ServiceSelectionScreen;
